import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import axios from "axios";
import { GoogleLogin } from "@react-oauth/google";
import { Icon } from "@iconify/react";
import defaultCoverWhite from 'assets/images/logos/defaultCoverWhite.png';

function Basic() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const ENVIRONMENT =
    apiUrl === 'https://dev.seatsnaps.com/'
      ? 'development'
      : apiUrl === 'https://production.seatsnaps.com/'
        ? 'production'
        : 'unknown';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const fullParams = window.location.search;
  const params = new URLSearchParams(fullParams);
  let roleName = params.get('name') || '';
  let orgNameRaw = params.get('orgName') || '';
  let orgName = orgNameRaw;
  let token = '';
  // Check if orgName contains '?token='
  if (orgNameRaw.includes('?token=')) {
    [orgName, token] = orgNameRaw.split('?token=');
  } else {
    token = params.get('token') || '';
  }
  let isEmailVerified = false;
  let isAdmin = false;
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    try {
      const response = await axios.post(`${apiUrl}auth-service/auth/login`, {
        username: email, password: password
      });

      if (response.data.success) {
        toast.success('You have successfully logged-in!');
        localStorage.setItem('token', response.data.data.token);
        const bearerToken = response.data.data.token

        if (bearerToken) {
          const userResponse = await axios.get(`${apiUrl}auth-service/auth/my`, { headers: { 'Authorization': `Bearer ${bearerToken}` } });
          isEmailVerified = userResponse.data.data.emailVerified;
          isAdmin = userResponse.data.data.isAdmin;
          localStorage.setItem('userEmail', userResponse.data.data.email);
        }
        setTimeout(() => {
          if (token && isEmailVerified) {
            navigate(`/userRoles/add-userRoles?token=${token}&roleName=${roleName}&orgName=${orgName}`);
          } else if (!isAdmin && !isEmailVerified) {
            navigate(`/verifyEmail?token=${token}&name=${roleName}&orgName=${orgName}`);
          }
          else {
            navigate(`/dashboard`);
          }
        }, 1000);

      }

    } catch (error) {
      console.error('Sign-in error:', error.message);
      toast.error(error.response?.data?.message || 'Log-In Failed!');
    }
  };

  const sendResetLink = async () => {
    if (!validateEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }
    try {
      setIsLoading(true);
      const resetPasswordLink = ENVIRONMENT === 'production'
        ? 'https://admin.seatsnaps.com/authentication/reset-password'
        : 'https://dev-admin.seatsnaps.com/authentication/reset-password';
      const response = await axios.post(`${apiUrl}auth-service/auth/forgot-password`,
        {
          email:
            email, resetPasswordLink: resetPasswordLink
        },
      );
      if (response.data?.success) {
        alert(response.data.message || "Reset link has been successfully sent!");
        console.log("Password Reset link successfully sent" || response.data.message);
      } else {
        throw new Error(response?.data?.message || "Unable to send reset link")
      }
    } catch (error) {
      console.error(error.message);
      alert("Unable to send reset link");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen w-full">
      {/* Left section - Dark background */}
      <div className="w-full md:w-2/3 bg-slate-800 flex flex-col justify-end">
        <div className="max-w-md mx-8 my-16">
          <h1 className="text-white text-3xl font-bold mb-6">Manage Your Events</h1>
          <p className="text-white mb-6">Easily manage, track, and organize all your events from one powerful dashboard. Streamline your workflow and make event planning simpler, smarter, and more efficient.</p>

          {/* Indicator dots */}
          {/* <div className="flex space-x-2 mt-6">
            <div className="h-1 w-8 bg-gray-400 rounded"></div>
            <div className="h-1 w-8 bg-white rounded"></div>
            <div className="h-1 w-8 bg-gray-400 rounded"></div>
          </div> */}
        </div>
      </div>

      {/* Right section - White background */}
      <div className="w-full md:w-1/3 bg-white p-12 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center">
              <img
                src={defaultCoverWhite}
                alt="Logo"
                className="h-24"
              />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-center mb-8 text-slate-800">Login to Your Account</h2>

          <form className="space-y-6">
            {/* Username/Email field */}
            <div>
              <label htmlFor="email" className="block text-slate-700 mb-2">
                Username
              </label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
              />
              {emailError && (<p className="text-red-500 text-sm mt-1 ml-1">{emailError}</p>)}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-400"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <Icon icon="mdi-light:eye" width="24" height="24" /> : <Icon icon="mdi-light:eye-off" width="24" height="24" />}
                </button>
              </div>
            </div>

            {/* Remember me and Forgot Password */}
            <div className="flex items-center justify-between">
              <p
                onClick={!isLoading ? sendResetLink : undefined}
                className={`text-sm text-slate-700 hover:underline ${isLoading ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                role="button"
                aria-disabled={isLoading}
                tabIndex={0}
              >
                Forgot Password?
              </p>

            </div>

            {/* Login button */}
            <button
              type="submit"
              onClick={handleSignIn}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-500 transition duration-200"
            >
              Login
            </button>
          </form>

          {/* <div className="w-full flex items-center justify-between mt-4">
            <p className="text-sm text-slate-700">
              Don&apos;t you have an account?{" "}
              <span
                onClick={() => navigate('/authentication/sign-up')}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                Sign Up
              </span>
            </p>
          </div> */}

          {/* <div className="w-full flex items-center justify-between mt-4">
            <GoogleLogin
              onSuccess={async (credentialResponse) => {
                try {
                  const googleToken = credentialResponse?.credential;
                  if (!googleToken) {
                    toast.error("Google token not found!");
                    return;
                  }

                  const authResponse = await axios.post(
                    `${apiUrl}auth-service/auth/google/callback`,
                    { token: googleToken }
                  );

                  if (authResponse?.data?.success) {
                    toast.success(authResponse.data.message || "You have successfully signed-in!");
                    const bearerToken = authResponse.data.data.token;
                    localStorage.setItem("token", bearerToken);

                    const userResponse = await axios.get(
                      `${apiUrl}auth-service/auth/my`,
                      { headers: { Authorization: `Bearer ${bearerToken}` } }
                    );

                    const userEmail = userResponse?.data?.data?.email;
                    if (userEmail) {
                      localStorage.setItem("userEmail", userEmail);
                    }
                    setTimeout(() => {
                      navigate("/dashboard");
                    }, 1000);

                  } else {
                    toast.error(authResponse?.data?.message || "Authentication failed!");
                  }
                } catch (error) {
                  console.error("Sign-in error:", error);
                  toast.error(error.response?.data?.message || "Sign-In Failed!");
                }
              }}
              onError={() => {
                console.error("Google Login Failed");
                toast.error("Google Login Failed");
              }}
            />
          </div> */}

        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>

  );
}

export default Basic;
